import { formatCount } from '../format';
import { INTAKE_EXPIRED_MESSAGE, intakeFailureMessage } from '../intake-client';
import type { IntakeFailure, ParseWarning } from '../types';
import type { PhonePlatform } from './environment';

/** Korean copy shared by several screens. Keep it observational and kind. */

export const TOOL_TITLE = '카톡 성적표';
export const TOOL_SUBTITLE = '카톡 대화를 올리면 선톡·답장 속도·새벽 대화·말버릇까지 성적표로 정리해 드려요.';
export const PRIVACY_LINE = '대화는 이 기기 안에서만 분석해요.';
export const NAME_IMAGE_WARNING = '이미지에 이름이 들어가요. 공유 전에 이름을 확인하세요.';

export function warningMessage(warning: ParseWarning): string {
  switch (warning.code) {
    case 'PC_EXPORT_MAY_BE_PARTIAL':
      return 'PC에서 내보낸 파일에는 채팅방에 불러온 메시지만 들어 있어요. 오래된 대화는 빠졌을 수 있어요.';
    case 'MULTIPART_MISSING_PARTS':
      // Also raised when a numbered part was picked but could not be decoded.
      return '나눠 저장된 대화 파일 중 일부가 빠졌거나 읽을 수 없었어요. 빠진 기간은 성적에 들어가지 않아요.';
    case 'UNPARSED_LINES':
      return warning.count
        ? `읽을 수 없는 줄 ${formatCount(warning.count)}개는 건너뛰었어요.`
        : '읽을 수 없는 줄 몇 개는 건너뛰었어요.';
    case 'TRIMMED_TO_RECENT':
      return warning.count
        ? `대화가 아주 길어서 오래된 메시지 ${formatCount(warning.count)}개는 빼고 최근 메시지만 분석해요.`
        : '대화가 아주 길어서 최근 메시지만 분석해요.';
    case 'EXPORTER_LABEL_DETECTED':
      return '‘회원님’처럼 표시된 이름은 보통 대화를 내보낸 사람(나)이에요.';
  }
}

/** Extra guidance under a parser error message. */
export function parseErrorHelp(code: string): string | null {
  switch (code) {
    case 'NO_FILES':
    case 'UNSUPPORTED_FILE':
    case 'NOT_KAKAO_EXPORT':
    case 'EMPTY_EXPORT':
    case 'ZIP_NO_TEXT':
    case 'ZIP_CORRUPT':
    case 'ENCODING_UNREADABLE':
      return '아래 ‘대화 내보내는 방법’을 따라 다시 받아 보세요. 휴대폰이라면 ‘메일로 보내기’가 가장 간단해요.';
    case 'FILE_TOO_LARGE':
      return '휴대폰에서 ‘텍스트 메시지만’으로 내보내면 사진·동영상이 빠져서 파일이 훨씬 작아져요.';
    case 'TOO_FEW_MESSAGES':
      return '대화가 조금 더 쌓인 뒤에 다시 와 주세요. 샘플로 결과 모양을 먼저 볼 수도 있어요.';
    case 'SINGLE_SENDER':
      return '나와의 채팅이나 알림 방은 성적을 매길 수 없어요.';
    case 'DIFFERENT_CHATS':
      return '여러 파일을 고를 때는 같은 대화방에서 나눠 저장된 파일만 함께 골라 주세요.';
    default:
      return null;
  }
}

// ---------------------------------------------------------------------------
// E-mail intake (spec §7.1)
// ---------------------------------------------------------------------------

export { INTAKE_EXPIRED_MESSAGE };
export const INTAKE_RETRY_HINT = '같은 주소로 다시 보내면 돼요.';
/** Shown in a tab whose address another open tab took over (one tab per address). */
export const EMAIL_ELSEWHERE_NOTE = '다른 탭에서 이 메일 주소로 메일을 기다리고 있어요.';
export const EMAIL_ELSEWHERE_ACTION = '이 탭에서 이어서 기다리기';
/** Toast once the server confirmed that the address (and anything it received) is deleted. */
export const EMAIL_DELETED_MESSAGE = '메일 주소를 지웠어요.';
/**
 * Toast when the delete did not reach the server (offline, service down) or the
 * server has not answered within DISCARD_CONFIRM_WAIT_MS (intake-api.ts). The
 * page sends it again once the connection is back; the server drops the
 * address 60 minutes after it was issued in any case (privacy policy §7).
 */
export const EMAIL_DELETE_PENDING_MESSAGE = '아직 주소를 지우지 못했어요. 연결되면 지우고, 늦어도 주소를 받은 지 60분이 지나면 지워요.';
/** Waiting card, when this browser could not store the address (site data blocked): a reload loses it. */
export const EMAIL_NOT_REMEMBERED_NOTE = '이 브라우저는 주소를 기억하지 못해요. 메일을 보낸 뒤 이 탭으로 돌아와 주세요. 페이지가 새로고침되면 주소가 사라지니, 그때는 ‘파일로 올리기’를 이용해 주세요.';

/** The waiting card's note about storage: only when the address could not be remembered. */
export function intakeStorageNote(remembered: boolean): string | null {
  return remembered ? null : EMAIL_NOT_REMEMBERED_NOTE;
}

/** Toast for a file dropped on a screen that does not take files (the tab must not open it). */
export function fileDropRefusedMessage(onResultScreen: boolean): string {
  return onResultScreen
    ? '다른 대화를 분석하려면 ‘다른 대화 분석하기’를 눌러 주세요.'
    : '대화 파일은 첫 화면에서 올릴 수 있어요.';
}

/** lastFailure copy from the intake client (spec §7.1) plus the retry hint. */
export function intakeFailureCopy(failure: IntakeFailure): { readonly message: string; readonly hint: string | null } {
  return {
    message: intakeFailureMessage(failure),
    // A link-only mail needs the file route, not another e-mail.
    hint: failure === 'LINK_ONLY' ? null : INTAKE_RETRY_HINT,
  };
}

/** "58분 남았어요" — rounds up so a fresh 60-minute address reads "60분". */
export function intakeRemainingLabel(expiresAt: number, now: number): string {
  const remaining = expiresAt - now;
  if (!(remaining > 0)) return '곧 만료돼요';
  const minutes = Math.ceil(remaining / 60_000);
  if (minutes <= 1) return '1분 안에 만료돼요';
  return `${minutes}분 남았어요`;
}

export interface GuideStep {
  readonly title: string;
  readonly detail?: string;
}

const SHARED_EMAIL_START: readonly GuideStep[] = [
  { title: '위 주소 복사하기', detail: '‘주소 복사’를 누르면 메일에 바로 붙여넣을 수 있어요.' },
  { title: '카카오톡에서 분석할 채팅방 열기' },
  { title: '오른쪽 위 ≡ → ⚙ 채팅방 설정' },
  { title: '대화 내용 내보내기 → 텍스트 메시지만' },
];

export const EMAIL_STEPS: Readonly<Record<PhonePlatform, readonly GuideStep[]>> = {
  iphone: [
    ...SHARED_EMAIL_START,
    {
      title: '메일 앱에서 받는 사람에 붙여넣기',
      detail: '카톡은 iPhone 기본 메일 앱으로 보내요. 메일 앱에 계정이 없다면 먼저 설정에서 Gmail이나 네이버 계정을 추가해 주세요.',
    },
    { title: '보내기 → 이 페이지로 돌아오기', detail: '메일이 도착하면 바로 성적표를 만들기 시작해요.' },
  ],
  android: [
    ...SHARED_EMAIL_START,
    { title: '메일 앱 고르고 받는 사람에 붙여넣기', detail: 'Gmail, 네이버 메일 등 평소 쓰는 메일 앱을 고르면 돼요.' },
    { title: '보내기 → 이 페이지로 돌아오기', detail: '메일이 도착하면 바로 성적표를 만들기 시작해요.' },
  ],
};

export const EMAIL_SENT_COPY_TIP = '팁: 보낸 메일함에 사본이 남아요. 필요하면 지워 주세요.';

export interface ExportGuide {
  readonly id: string;
  readonly title: string;
  readonly steps: readonly string[];
  readonly note?: string;
}

export const EXPORT_GUIDES: readonly ExportGuide[] = [
  {
    id: 'windows',
    title: 'PC (Windows)',
    steps: [
      'PC 카카오톡에서 채팅방 열기',
      'Ctrl+S를 누르거나, 오른쪽 위 ≡ → 대화 내용 → 대화 내보내기',
      '저장된 .txt 파일을 위에서 올리기',
    ],
  },
  {
    id: 'mac',
    title: 'Mac',
    steps: [
      '채팅방 오른쪽 위 ≡ → 채팅방 설정',
      '대화 내용 관리 → 대화 내용 저장',
      '저장된 .txt(또는 .csv) 파일을 위에서 올리기',
    ],
  },
  {
    id: 'phone',
    title: 'Android · iPhone',
    steps: [
      '채팅방 오른쪽 위 ≡ → ⚙ 채팅방 설정',
      '대화 내용 내보내기 → 텍스트 메시지만',
      '파일로 저장해서 올리거나, ‘메일로 보내기’ 주소로 보내기',
    ],
    note: '사진·동영상까지 내보내면 파일이 아주 커져요. ‘텍스트 메시지만’을 골라 주세요.',
  },
];

export const PC_PARTIAL_NOTE = 'PC에는 불러온 메시지만 저장돼요. 오래된 대화까지 보려면 채팅방을 위로 끝까지 올린 뒤 저장하거나, 휴대폰에서 내보내 주세요.';
